import {
    updateAllocationDimensionAttributeRequest,
    addAllocationDimensionAttributeAndUpdateValueRequest,
} from "actions/allocation-dimension";
import { IAction } from "epics/base-epic";
import {
    AllocationDimensionIndex,
    AllocationDimensionKind,
    generateAllocationDimensionAttribute,
    getAllocationDimensionByIndex,
    IAllocationDimension,
} from "helpers/allocation-dimension";
import { ISchemaKey } from "helpers/product-collection";
import React from "react";
import { connect, Dispatch } from "react-redux";
import { IStore } from "reducers";
import {
    getAllocationDimensionAttributes,
    getAllocationDimensionsBySubtype,
    getDoorClassificationSchema,
} from "reducers/selectors/collection";
import { CreateDoorAttributesDialogComponent } from "./create-door-attributes-dialog";
import { formatMessage } from "lib/globalize-util";

import {
    ALTERNATIVE_KEY_COLUMN_FIELD,
    READ_ONLY_COLUMNS,
} from "components/product-collection/manage-allocation-dimension/import-door-attributes/constants";
import { sanitizeDimensionAttribute } from "../../utils";
import { buildColumnExpression } from "helpers/utils/build-column-expression";

interface IDispatchProps {
    updateAllocationDimensionAttribute?: (
        allocationDimensionAttribute: ISchemaKey[],
        allocationDimensionId: string
    ) => void;
    onCustomDoorCellValueChanged?: (
        attributes: ISchemaKey[],
        allocationDimensionId: string,
        rowIndexToDataMap: any
    ) => void;
}

interface IStateProps extends IDispatchProps {
    objectId?: string;
    allocationDimension?: IAllocationDimension;
    allocationDimensionAttributes?: ISchemaKey[];
    doorClassificationSchema?: ISchemaKey[];
}
interface IOwnProps {
    onCancel?: () => void;
    onCancelConfirmDialog?: () => void;
    onDeleteDoorGrouping?: (id: string) => void;
    onEditDoorGroupingRequest?: () => void;
    attributeKey?: string;
    isDialogOpen?: boolean;
    isConfirmDialogOpen?: boolean;
}
type IProps = IOwnProps & IDispatchProps & IStateProps;

const CreateDoorAttributesDialogContainer: React.FunctionComponent<IProps> = ({
    doorClassificationSchema,
    allocationDimensionAttributes,
    updateAllocationDimensionAttribute,
    onCancel,
    onCancelConfirmDialog,
    objectId,
    attributeKey,
    isDialogOpen,
    isConfirmDialogOpen,
    onEditDoorGroupingRequest,
    allocationDimension,
    onCustomDoorCellValueChanged,
}) => {
    const [doorGroupingNameError, setDoorGroupingNameError] = React.useState<string>(undefined);

    const doorGroupingOptions = React.useMemo(() => {
        return doorClassificationSchema.map((doorGrouping) => {
            const doorGroupingValues = doorGrouping?.constraint?.allowed_values
                ?.filter((element) => element.string_value !== "")
                .map((groupingEntry) => ({
                    text: groupingEntry.string_value,
                    key: groupingEntry.string_value,
                    id: groupingEntry.string_value,
                    doorGroupingId: doorGrouping.field_name,
                }));
            return { id: doorGrouping.field_name, name: doorGrouping.display_name, values: doorGroupingValues };
        });
    }, [doorClassificationSchema]);

    const handleDeleteDoorGrouping = React.useCallback(
        (id: string) => {
            let newAllocationDimensionAttributes = [];
            let allocationDimensionAttributesCopy = [...allocationDimensionAttributes];

            allocationDimensionAttributesCopy = allocationDimensionAttributesCopy.filter(
                (attribute) => attribute.field_name !== id
            );
            newAllocationDimensionAttributes = [...allocationDimensionAttributesCopy];

            let rowIndexToDataMap;
            allocationDimension?.dimension_values.forEach((row, index) => {
                if (row.fields?.[id]) {
                    if (!rowIndexToDataMap) {
                        rowIndexToDataMap = new Map<number, any>();
                    }
                    const rowToUpdate = { ...row };
                    delete rowToUpdate.fields[id];
                    rowIndexToDataMap.set(index, rowToUpdate);
                }
            });
            if (rowIndexToDataMap) {
                // deletes attribute and updates rows
                onCustomDoorCellValueChanged(newAllocationDimensionAttributes, objectId, rowIndexToDataMap);
            } else {
                // just deletes attibute
                updateAllocationDimensionAttribute(newAllocationDimensionAttributes, objectId);
            }
            onCancelConfirmDialog();
        },
        [
            onCustomDoorCellValueChanged,
            allocationDimension?.dimension_values,
            allocationDimensionAttributes,
            objectId,
            onCancelConfirmDialog,
            updateAllocationDimensionAttribute,
        ]
    );

    const handleSaveDoorGrouping = React.useCallback(
        (groupName: string) => {
            const nameMatchFound = doorGroupingOptions.find(
                (doorGrouping) => doorGrouping.name.toLowerCase() === groupName.toLowerCase()
            );
            const expression = buildColumnExpression(groupName);

            if (groupName.trim() === "") {
                setDoorGroupingNameError(formatMessage("Name is required."));
            } else if (nameMatchFound) {
                setDoorGroupingNameError(formatMessage("A grouping with that name already exists in this assortment."));
            } else if ([...READ_ONLY_COLUMNS, ALTERNATIVE_KEY_COLUMN_FIELD].some((field) => expression.test(field))) {
                setDoorGroupingNameError(
                    formatMessage(
                        "This name is reserved in our system. Please choose a different name for your grouping."
                    )
                );
            } else {
                setDoorGroupingNameError(undefined);
                let newAllocationDimensionAttributes = [];

                let rowIndexToDataMap;
                if (attributeKey) {
                    const doorAttribute = allocationDimensionAttributes.find(
                        (attribute: ISchemaKey) => attribute.field_name.toLowerCase() === attributeKey
                    );
                    const doorAttributeIndex = allocationDimensionAttributes.findIndex(
                        (attribute: ISchemaKey) => attribute.field_name.toLowerCase() === attributeKey
                    );
                    const fieldName = sanitizeDimensionAttribute.fieldName(groupName);
                    allocationDimension?.dimension_values.forEach((row, index) => {
                        if (row.fields?.[attributeKey]) {
                            if (!rowIndexToDataMap) {
                                rowIndexToDataMap = new Map<number, any>();
                            }

                            const rowToUpdate = { ...row };
                            rowToUpdate.fields[fieldName] = rowToUpdate.fields[attributeKey];
                            delete rowToUpdate.fields[attributeKey];
                            rowIndexToDataMap.set(index, rowToUpdate);
                        }
                    });

                    const modifiedAllocationDimensionAttribute = Object.assign({}, doorAttribute, {
                        display_name: sanitizeDimensionAttribute.displayName(groupName),
                        field_name: fieldName,
                    });

                    newAllocationDimensionAttributes = [...allocationDimensionAttributes];
                    newAllocationDimensionAttributes.splice(
                        doorAttributeIndex,
                        1,
                        modifiedAllocationDimensionAttribute
                    );
                } else {
                    newAllocationDimensionAttributes = [
                        ...allocationDimensionAttributes,
                        generateAllocationDimensionAttribute(groupName),
                    ];
                }

                if (rowIndexToDataMap) {
                    onCustomDoorCellValueChanged(newAllocationDimensionAttributes, objectId, rowIndexToDataMap);
                } else {
                    updateAllocationDimensionAttribute(newAllocationDimensionAttributes, objectId);
                }

                onCancel();
                onEditDoorGroupingRequest?.();
            }
        },
        [
            allocationDimensionAttributes,
            attributeKey,
            doorGroupingOptions,
            objectId,
            onCancel,
            updateAllocationDimensionAttribute,
            onEditDoorGroupingRequest,
            onCustomDoorCellValueChanged,
            allocationDimension?.dimension_values,
        ]
    );

    return (
        <CreateDoorAttributesDialogComponent
            onSaveDoorGrouping={(groupName: string) => handleSaveDoorGrouping(groupName)}
            onCancel={onCancel}
            attributeKey={attributeKey}
            doorGroupingOptions={doorGroupingOptions}
            doorGroupingNameError={doorGroupingNameError}
            isDialogOpen={isDialogOpen}
            isConfirmDialogOpen={isConfirmDialogOpen}
            onCancelConfirmDialog={onCancelConfirmDialog}
            onDeleteDoorGrouping={handleDeleteDoorGrouping}
        />
    );
};

const mapStateToProps = (state: IStore, _: IOwnProps): IStateProps => {
    const collectionId = state.collection?.collectionId;

    const genericAllocationDimensions = getAllocationDimensionsBySubtype(
        state,
        collectionId,
        AllocationDimensionKind.Generic
    );
    const allocationDimension = getAllocationDimensionByIndex(
        genericAllocationDimensions,
        AllocationDimensionIndex.Door
    );
    const allocationDimensionAttributes = getAllocationDimensionAttributes(
        state,
        collectionId,
        AllocationDimensionIndex.Door
    );
    const doorClassificationSchema = getDoorClassificationSchema(state, collectionId);

    return {
        objectId: allocationDimension.id,
        allocationDimension: allocationDimension,
        allocationDimensionAttributes,
        doorClassificationSchema,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IAction<any>>): IDispatchProps => {
    return {
        updateAllocationDimensionAttribute: (
            allocationDimensionAttribute: ISchemaKey[],
            allocationDimensionId: string
        ) => dispatch(updateAllocationDimensionAttributeRequest(allocationDimensionAttribute, allocationDimensionId)),
        onCustomDoorCellValueChanged: (
            attributes: ISchemaKey[],
            allocationDimensionId: string,
            rowIndexToDataMap: any
        ) =>
            dispatch(
                addAllocationDimensionAttributeAndUpdateValueRequest(
                    attributes,
                    allocationDimensionId,
                    rowIndexToDataMap
                )
            ),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateDoorAttributesDialogContainer);
